import { Injectable } from '@angular/core';
import { QuoteHeader, QuoteDetail } from '@quorum/models/xs-resource';
import { Actions, createEffect } from '@ngrx/effects';
import { DataPersistence } from '@nrwl/angular';
import { ApiService } from '@quorum/api';
import * as fromQuotes from './quotes.actions';
import { QuotesState } from './quotes.interfaces';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FixedOperationsStateService } from '../../fixed-operations-state.service';
import { of, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

const QUOTE_LINE_ERROR_TOAST_DURATION_MS = 6000;

interface ToastOptions {
  message: string;
  actionText: string | null;
  options: { duration: number };
}

function quoteLineErrorToast(error: unknown): ToastOptions {
  const reason = (error as { reason?: unknown } | null)?.reason;
  const detail = typeof reason === 'string' && reason.trim() !== '' ? `: ${reason}` : '';
  return {
    message: `Line could not be added to quote${detail}`,
    actionText: null,
    options: { duration: QUOTE_LINE_ERROR_TOAST_DURATION_MS },
  };
}

@Injectable()
export class QuotesEffects {
  
  getQuotesFromServer = createEffect(() => this.dataPersistence.fetch(fromQuotes.GET_QUOTES_FROM_SERVER, {
    run: (action: fromQuotes.GetQuotesFromServer, state: QuotesState) => {
      return this.apiService
        .get<QuoteHeader[]>(`v/1/fixed-operations/quote-headers`, { params: action.payload })
        .pipe(map((quotes) => new fromQuotes.GetQuotesFromServerSuccess({ quotes: quotes })));
    },
    onError: (action: fromQuotes.GetQuotesFromServer, error) => {
      console.error('Error', error);
    },
  }));

  
  getQuoteFromServer = createEffect(() => this.dataPersistence.fetch(fromQuotes.GET_QUOTE_FROM_SERVER, {
    run: (action: fromQuotes.GetQuoteFromServer, state: QuotesState) => {
      return this.apiService.get<QuoteHeader[]>(`v/1/fixed-operations/quote-headers/${action.payload.id}`).pipe(
        map((quote) => {
          if (quote != null) {
            return new fromQuotes.GetQuoteFromServerSuccess({ quote: quote });
          }
        })
      );
    },
    onError: (action: fromQuotes.GetQuoteFromServer, error) => {
      console.error('Error', error);
    },
  }), { dispatch: false });

  
  createQuoteLine = createEffect(() => this.dataPersistence.pessimisticUpdate(fromQuotes.POST_QUOTE_CASE_TO_SERVER, {
    run: (action: fromQuotes.PostQuoteCaseToServer, state: any) => {
      let workFlowParams: any = {};
      if (action.payload.case.serviceGroupId != null) {
        workFlowParams.serviceGroupId = action.payload.case.serviceGroupId;
      }
      return this.apiService
        .get<any>(
          `v/1/fixed-operations/quote-headers/${action.payload.case.quoteHeaderId}/workflow/calculate-labour-rate/${action.payload.case.warrantySubmissionTypeId}`,
          {
            params: workFlowParams,
          }
        )
        .pipe(
          switchMap((labourRate: any) => {
            let caseDetails = JSON.parse(JSON.stringify(action.payload.case));
            caseDetails.transactionTypeId = labourRate.labourTransType;
            return this.apiService.post<QuoteDetail>(`v/1/fixed-operations/quote-details`, caseDetails).pipe(
              map((quoteCase) => {
                let toastOptions: any = {
                  message: 'Line added to quote',
                  actionText: null,
                  options: { duration: 3000 },
                };
                this.fixedOperationsStateService.addToast(toastOptions);
                return new fromQuotes.PostQuoteCaseToServerSuccess({ case: quoteCase });
              })
            );
          })
        );
    },
    onError: (action: fromQuotes.PostQuoteCaseToServer, error) => {
      console.error('Error', error);
      this.fixedOperationsStateService.addToast(quoteLineErrorToast(error));
    },
  }));

  
  createQuoteAndQuoteLine = createEffect(() => this.dataPersistence.pessimisticUpdate(fromQuotes.POST_QUOTE_AND_CASE_TO_SERVER, {
    run: (action: fromQuotes.PostQuoteAndCaseToServer, state: any) => {
      return this.apiService.post<QuoteDetail>(`v/1/fixed-operations/quote-headers`, action.payload.header).pipe(
        switchMap((quoteHeader) => {
          let quoteCase: QuoteDetail = { ...action.payload.case };
          quoteCase.quoteHeaderId = quoteHeader.id;
          let workFlowParams: any = {};
          if (quoteCase.serviceGroupId != null) {
            workFlowParams.serviceGroupId = quoteCase.serviceGroupId;
          }
          return this.apiService
            .get<any>(
              `v/1/fixed-operations/quote-headers/${quoteCase.quoteHeaderId}/workflow/calculate-labour-rate/${quoteCase.warrantySubmissionTypeId}`,
              {
                params: workFlowParams,
              }
            )
            .pipe(
              switchMap((labourRate: any) => {
                let caseDetails = JSON.parse(JSON.stringify(action.payload.case));
                caseDetails.transactionTypeId = labourRate.labourTransType;
                return this.apiService.post<QuoteDetail>(`v/1/fixed-operations/quote-details`, caseDetails).pipe(
                  switchMap((quoteCase) => {
                    let toastOptions: any = {
                      message: 'Line added to quote',
                      actionText: null,
                      options: { duration: 3000 },
                    };
                    this.fixedOperationsStateService.addToast(toastOptions);
                    return from([
                      new fromQuotes.PostQuoteCaseToServerSuccess({ case: quoteCase }),
                      new fromQuotes.GetQuoteFromServerSuccess({ quote: quoteHeader }),
                    ]);
                  })
                );
              })
            );
        })
      );
    },
    onError: (action: fromQuotes.PostQuoteAndCaseToServer, error) => {
      console.error('Error', error);
      this.fixedOperationsStateService.addToast(quoteLineErrorToast(error));
    },
  }));

  constructor(
    private actions: Actions,
    private dataPersistence: DataPersistence<QuotesState>,
    private apiService: ApiService,
    private fixedOperationsStateService: FixedOperationsStateService
  ) {}
}
